import { useEffect } from "react";
import { X, MessageCircle, ArrowUpRight } from "lucide-react";
import { BRANCHES, buildWhatsAppLink } from "../lib/constants";
import { useTranslation } from "../lib/i18n";

/**
 * QuoteBranchModal — country-selection popup for the "Get a Quote" CTA.
 * Two large branch cards; picking one opens that branch's WhatsApp in a
 * new tab with a prefilled quote message.
 */
export const QuoteBranchModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handlePick = (branch) => {
    const message = `Hello Beyond Borders Travel (${branch.city}, ${branch.country}),\n\nI'd like to request a quote for my upcoming trip.\n\nPlease get back to me at your earliest convenience.`;
    const url = buildWhatsAppLink(message, branch.whatsappNumber);
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-modal-title"
      data-testid="quote-branch-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label={t("quote.modal.close")}
        onClick={onClose}
        data-testid="quote-modal-backdrop"
        className="absolute inset-0 bg-[#05070C]/85 backdrop-blur-md"
      />

      {/* Card */}
      <div className="relative w-full max-w-2xl glass-strong p-6 sm:p-10 lg:p-12 max-h-[90vh] overflow-y-auto">
        <div className="absolute top-0 left-0 h-px w-24 bg-gold" />

        <button
          type="button"
          onClick={onClose}
          data-testid="quote-modal-close"
          aria-label={t("quote.modal.close")}
          className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-gold hover:border-gold/60 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-3 flex items-center gap-2">
          <span className="h-px w-8 bg-gold/70" />
          {t("nav.getQuote")}
        </div>
        <h2
          id="quote-modal-title"
          className="font-display text-3xl sm:text-4xl text-white leading-tight"
        >
          {t("quote.modal.title")}
        </h2>
        <p className="mt-3 text-white/60 text-sm sm:text-base max-w-lg">
          {t("quote.modal.subtitle")}
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {BRANCHES.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => handlePick(b)}
              data-testid={`quote-modal-branch-${b.id}`}
              className="group text-left relative overflow-hidden border border-white/10 hover:border-gold/70 bg-[#05070C]/40 hover:bg-gold/5 transition-all duration-300 p-6 sm:p-7"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-5xl leading-none mb-4" aria-hidden="true">
                    {b.flag}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold/80 mb-1.5">
                    {b.country}
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl text-white leading-tight">
                    {b.city}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">
                    {t("quote.modal.chatWith", { country: b.country })}
                  </p>
                </div>
                <span className="grid place-items-center h-10 w-10 rounded-full bg-gold text-gold-ink shrink-0 group-hover:scale-110 transition-transform">
                  <MessageCircle className="h-4 w-4" strokeWidth={2.2} />
                </span>
              </div>

              <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] text-white/50 group-hover:text-gold transition-colors">
                <span>{b.phones[0]}</span>
                <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuoteBranchModal;
